import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { useSmsWorkspaceSenders } from '@/features/campaigns/v2/Sms/context/SmsWorkspaceContext';
import RouteConstants from '@/features/router/RouteConstants';
import { Button } from '@/lib';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaIdBadge } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { countSendersByStatus } from './senderUtils';
import { SmsSenderListErrorState } from './SmsSenderListErrorState';
import { SmsSenderListHeader } from './SmsSenderListHeader';
import { SmsSenderListLoadingState } from './SmsSenderListLoadingState';
import { SmsSenderListStats } from './SmsSenderListStats';
import { SmsSenderTable } from './SmsSenderTable';

const ConnectorNotConnectedBlock: FC = () => {
  const history = useHistory();
  const { t } = useTranslation('campaign');

  return (
    <div className='flex flex-col items-center justify-center gap-6 rounded-lg border border-gray-400 py-20'>
      <h2 className='text-4xl font-semibold text-slate-600'>{t('smsWorkspace.senders.noConnectorTitle')}</h2>
      <p className='text-secondary-600 max-w-xl text-center'>{t('smsWorkspace.senders.noConnectorDescription')}</p>
      <FaIdBadge className='size-12 text-gray-400' />
      <Button onClick={() => history.push(RouteConstants.destinations)} text={t('goToDestinationsText')} />
    </div>
  );
};

export const SmsSenderList: FC = () => {
  const history = useHistory();
  const { isAudienceSettled, hasAudience, isLoading: isAudienceLoading } = useFirstAudienceId();
  const {
    senders,
    isLoading,
    isError,
    reload,
    missingInstance,
    missingAudience,
    isInsertReady,
    isEditReady,
    isDeleteReady,
    deleteFlowId,
    connectorInstanceId,
  } = useSmsWorkspaceSenders();

  const canRegister = !isLoading && isInsertReady;
  const stats = useMemo(() => countSendersByStatus(senders), [senders]);

  const statsItems = useMemo(
    () => [
      {
        key: 'approved',
        labelKey: 'smsWorkspace.senders.stats.approved',
        value: stats.approved,
        status: 'APPROVED' as const,
      },
      {
        key: 'inRegistration',
        labelKey: 'smsWorkspace.senders.stats.inRegistration',
        value: stats.inRegistration,
        status: 'IN_REGISTRATION' as const,
      },
      {
        key: 'rejected',
        labelKey: 'smsWorkspace.senders.stats.rejected',
        value: stats.rejected,
        status: 'REJECTED' as const,
      },
      {
        key: 'drafts',
        labelKey: 'smsWorkspace.senders.stats.drafts',
        value: stats.drafts,
        status: 'DRAFT' as const,
      },
    ],
    [stats]
  );

  const handleRegister = () => {
    if (!canRegister) {
      return;
    }
    history.push(RouteConstants.createSmsSender);
  };

  const handleDeleted = useCallback(() => {
    void reload();
  }, [reload]);

  if (isAudienceLoading) {
    return <SmsSenderListLoadingState />;
  }

  if (missingAudience || (isAudienceSettled && !hasAudience)) {
    return (
      <section className='space-y-6 rounded-3xl border border-secondary-border bg-surface p-5 shadow-sm md:p-6'>
        <SmsSenderListHeader onRegister={handleRegister} isRegisterDisabled />
        <AudienceRequiredEmptyState />
      </section>
    );
  }

  if (missingInstance) {
    return (
      <section className='space-y-6 rounded-3xl border border-secondary-border bg-surface p-5 shadow-sm md:p-6'>
        <SmsSenderListHeader onRegister={handleRegister} isRegisterDisabled />
        <ConnectorNotConnectedBlock />
      </section>
    );
  }

  const showError = !isLoading && isError;

  return (
    <section className='space-y-6 rounded-3xl border border-secondary-border bg-surface p-5 shadow-sm md:p-6'>
      <SmsSenderListHeader onRegister={handleRegister} isRegisterDisabled={!canRegister} />

      {isLoading ? <SmsSenderListLoadingState /> : null}

      {showError ? <SmsSenderListErrorState onRetry={() => void reload()} /> : null}

      {!isLoading && !showError ? (
        <>
          <SmsSenderListStats stats={statsItems} />
          <SmsSenderTable
            senders={senders}
            deleteFlowId={deleteFlowId}
            connectorInstanceId={connectorInstanceId}
            isEditReady={isEditReady}
            isDeleteReady={isDeleteReady}
            onDeleted={handleDeleted}
          />
        </>
      ) : null}
    </section>
  );
};
